/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import { Box, Typography, Link } from '@mui/material';
import { ClassNameMap } from '@mui/styles/';
import InfoIcon from '@mui/icons-material/Info';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { getTerminalsOnce } from '../../../store/terminals/actions';
import { selectTerminals, selectMissionPlanById, selectCustomerSupportEmail } from '../../../store';
import { setSelectedTerminal } from '../../../store/missionPlans/actions';
import { FormikFieldEnum, FormValues, useAutopopulateFormikValue } from './MissionDetailsForm';
import SelectBox from './SelectBox';
import Terminal from '../../../shared/entities/terminal';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { sortAlphabetically } from '../../../utils/StringUtils';
import { isNil } from 'lodash';
import appTheme from '../../common/theme/appTheme.json';

export interface TailSelectFieldProps {
  styling: ClassNameMap<'label' | 'input' | 'error'>;
}

const TailSelectField = (props: TailSelectFieldProps) => {
  const { styling } = props;
  const { values } = useFormikContext<FormValues>();
  const [terminalMapping, setTerminalMapping] = useState<{ [key: string]: string }>({});
  const [terminalTails, setTerminalTails] = useState<string[]>([]);
  const terminals: { [key: string]: Terminal } = useSelector(selectTerminals, shallowEqual);
  const missionPlanToEdit = useSelector(selectMissionPlanById, shallowEqual);
  const dispatch = useDispatch();

  const customerSupportEmail = useSelector(selectCustomerSupportEmail, shallowEqual);
  const missingTailEmailSubject = 'Unified Portal Support: Missing Tail Number in MPT';
  const missingTailEmailBody =
    "MISSING TAIL NUMBER: <insert tail number>\n\nTail number doesn't appear as an option when creating a mission plan in MPT. Check IP/MAC address and terminal name in NMS and verify with Unified Portal team.";

  // Get terminals when component mounts
  useEffect(() => {
    dispatch(getTerminalsOnce());
  }, [dispatch]);

  // Populates tail number field when editting a plan
  useAutopopulateFormikValue(
    FormikFieldEnum.TAIL_NUMBER,
    missionPlanToEdit.aircraftTailNum ? missionPlanToEdit.aircraftTailNum : '',
  );

  /*
   *  Creates a string array of all the terminal display names
   *  Creates a mapping between aircraftTailNumber and terminal id
   */
  useEffect(() => {
    const terminalTails: string[] = [];
    const compiledTerminalMapping: { [key: string]: string } = {};

    if (terminals !== undefined) {
      Object.keys(terminals).forEach(key => {
        // Get terminal from the given key
        const terminal = terminals[key];

        // Mission plan create/update fails when terminal doesn't have sed/sscf file versions or antennaTypeId
        if (!isNil(terminal.sedFileVer) && !isNil(terminal.sscfFileVer) && !isNil(terminal.antennaTypeId)) {
          // Add aircraftTailNumber for the terminal to the array
          terminalTails.push(terminal.aircraftTailNumber);

          // Add mapping from aircraftTailNumber to datapathUuid (to search for a specific terminal quickly from the store)
          compiledTerminalMapping[terminal.aircraftTailNumber] = terminal.datapathUuid;
        }
      });
    }
    // Save mapping in state
    setTerminalMapping(compiledTerminalMapping);

    //sort temrinals alphabetically
    sortAlphabetically(terminalTails);

    // Save array of terminal tails in state
    terminalTails.push(''); // Prevents console warning when SelectBox has empty value ''
    setTerminalTails(terminalTails);
  }, [terminals]);

  // Save the selected terminal in redux store when user selects a terminal
  useEffect(() => {
    if (terminals !== undefined) dispatch(setSelectedTerminal(terminals[terminalMapping[values.tailNumber]]));
  }, [values.tailNumber, dispatch, terminals, terminalMapping]);

  // Custom filter fcn to decide which options to display based on input
  const filter = (options: any[], state: FilterOptionsState<any>) => {
    return options.filter(option => option.toLowerCase().includes(state.inputValue.toLowerCase()));
  };

  return (
    <React.Fragment>
      <Box flexBasis="48%">
        <label htmlFor={FormikFieldEnum.TAIL_NUMBER} className={styling.label}>
          Tail Number*
        </label>
        <SelectBox options={terminalTails} fieldName={FormikFieldEnum.TAIL_NUMBER} isDisabled={false} filter={filter}>
          Select Tail
        </SelectBox>
        <Box sx={{ display: 'flex', marginTop: '8px', alignItems: 'center' }}>
          <InfoIcon sx={{ fontSize: 16, color: appTheme.mptFormLabelText, marginRight: '4px' }} />
          <Typography
            sx={{
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '20px',
              color: appTheme.mptFormLabelText,
            }}
          >
            If your tail is not listed,&nbsp;
            <Link
              color="#009DE0"
              underline="hover"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                window.location.href = `mailto:${
                  customerSupportEmail || ''
                }?subject=${missingTailEmailSubject}&body=${missingTailEmailBody.replace(/\n/g, '%0D')}`;
              }}
            >
              contact support
            </Link>
            .
          </Typography>
        </Box>
        <ErrorMessage component="div" name="tailNumber" className={styling.error} />
      </Box>
    </React.Fragment>
  );
};

export default TailSelectField;
